import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
    Volume2, ArrowLeft, Play, Pause, Square, Upload, Music, Trash2, 
    Wand2, Download, ToggleLeft, ToggleRight, Loader2, Save, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { useAuth } from '../contexts/AuthContext';
import { useMasteringEngine } from '../hooks/useMasteringEngine';
import { Waveform, SpectrumAnalyzer, LevelMeter } from '../components/mastering/Visualizers';
import { 
    EQModule, CompressorModule, LimiterModule, StereoModule, 
    ExciterModule, DeesserModule, OutputModule 
} from '../components/mastering/ProcessingModules';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Master() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { token, logout } = useAuth();
    const fileInputRef = useRef(null);
    
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const {
        initAudio,
        tracks,
        isPlaying,
        currentTime,
        duration,
        analysis,
        settings,
        bypass,
        setBypass,
        loadTrack,
        removeTrack,
        play,
        pause,
        stop,
        seek,
        updateSetting,
        applyRecommendations,
        getAnalyser
    } = useMasteringEngine();

    // Initialize audio on first interaction
    useEffect(() => {
        const handleInteraction = () => {
            initAudio();
            document.removeEventListener('click', handleInteraction);
        };
        document.addEventListener('click', handleInteraction);
        return () => document.removeEventListener('click', handleInteraction);
    }, [initAudio]);

    // Fetch project
    useEffect(() => {
        fetchProject();
    }, [projectId]);

    const fetchProject = async () => {
        try {
            const response = await axios.get(`${API_URL}/projects/${projectId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setProject(response.data);
            
            // Apply saved settings if any
            if (response.data.settings) {
                Object.entries(response.data.settings).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        updateSetting(key, value);
                    }
                });
            }
            
            // Apply AI recommendations if any
            if (response.data.ai_recommendations) {
                // Don't auto-apply, just show they exist
            }
        } catch (error) {
            console.error('Error fetching project:', error);
            if (error.response?.status === 401) {
                logout();
                navigate('/login');
            } else {
                toast.error('Failed to load project');
                navigate('/dashboard');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle file upload
    const handleFileUpload = async (file) => {
        if (!file.type.startsWith('audio/')) {
            toast.error('Please upload an audio file');
            return;
        }

        try {
            // Upload to server
            const formData = new FormData();
            formData.append('file', file);
            
            const uploadResponse = await axios.post(`${API_URL}/upload/audio`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });

            const trackInfo = {
                filename: uploadResponse.data.filename,
                original_name: uploadResponse.data.original_name || file.name,
                size: uploadResponse.data.size
            };

            // Load into audio engine
            await loadTrack(file, trackInfo);
            toast.success(`Loaded: ${file.name}`);
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Failed to upload file');
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) handleFileUpload(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileUpload(file);
    };

    // AI Analysis
    const handleAIAnalysis = async () => {
        if (tracks.length === 0) {
            toast.error('Upload a track first');
            return;
        }

        setAnalyzing(true);
        try {
            const response = await axios.post(`${API_URL}/ai/analyze`, {
                project_id: projectId,
                audio_analysis: analysis
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.recommendations) {
                applyRecommendations(response.data.recommendations);
                toast.success('AI recommendations applied!');
            }
        } catch (error) {
            console.error('Error analyzing:', error);
            toast.error('Analysis failed');
        } finally {
            setAnalyzing(false);
        }
    };

    // Save project
    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API_URL}/projects/${projectId}`, {
                tracks: tracks.map(t => ({
                    filename: t.filename,
                    original_name: t.original_name,
                    duration: t.duration,
                    size: t.size
                })),
                settings
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success('Project saved!');
        } catch (error) {
            console.error('Error saving:', error);
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    // Format time
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#D0F224]" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="master-page">
            {/* Header */}
            <header className="glass sticky top-0 z-50 border-b border-white/10">
                <div className="max-w-[1800px] mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#D0F224] flex items-center justify-center">
                                <Volume2 className="w-5 h-5 text-black" />
                            </div>
                            <div>
                                <h1 className="font-heading font-bold">{project?.name}</h1>
                                <span className="text-xs text-[#A1A1AA] capitalize">{project?.project_type}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Bypass Toggle */}
                        <Button
                            variant={bypass ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setBypass(!bypass)}
                            className={bypass ? 'bg-[#FF9F1C] text-black' : 'border-[#333]'}
                            data-testid="bypass-btn"
                        >
                            {bypass ? <ToggleRight className="w-4 h-4 mr-2" /> : <ToggleLeft className="w-4 h-4 mr-2" />}
                            {bypass ? 'Bypassed' : 'Processing'}
                        </Button>

                        {/* AI Analysis */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleAIAnalysis}
                            disabled={analyzing || tracks.length === 0}
                            className="border-[#7B2CBF] text-[#7B2CBF] hover:bg-[#7B2CBF]/10"
                            data-testid="ai-analyze-btn"
                        >
                            {analyzing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Wand2 className="w-4 h-4 mr-2" />
                            )}
                            AI Analyze
                        </Button>

                        {/* Save */}
                        <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-[#D0F224] text-black hover:bg-[#E2F568]"
                            data-testid="save-btn"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Save
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="p-4">
                <div className="mastering-console">
                    {/* Left Panel - Tracks & Analysis */}
                    <div className="space-y-4">
                        {/* Upload Zone */}
                        <div 
                            className={`upload-zone ${isDragging ? 'drag-active' : ''}`}
                            onDrop={handleDrop}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="upload-zone"
                        >
                            <Upload className="w-8 h-8 mx-auto mb-2 text-[#A1A1AA]" />
                            <p className="text-sm text-[#A1A1AA]">Drop audio or click to upload</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="audio/*"
                                className="hidden"
                                onChange={handleFileSelect}
                            />
                        </div>

                        {/* Track List */}
                        {tracks.length > 0 && (
                            <div className="control-section">
                                <div className="control-section-header">
                                    <h3 className="font-heading text-sm font-bold">Tracks</h3>
                                </div>
                                <div className="track-list">
                                    {tracks.map((track, index) => (
                                        <div key={index} className="track-item active">
                                            <Music className="w-4 h-4 text-[#D0F224]" />
                                            <span className="text-sm truncate flex-1">{track.original_name}</span>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="w-6 h-6 text-[#A1A1AA] hover:text-[#FF3B30]"
                                                onClick={() => removeTrack(index)}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Analysis Info */}
                        <div className="control-section">
                            <div className="control-section-header">
                                <h3 className="font-heading text-sm font-bold">Analysis</h3>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <span className="text-[#A1A1AA]">Peak</span>
                                    <div className="font-mono text-[#D0F224]">{analysis.peakLevel} dB</div>
                                </div>
                                <div>
                                    <span className="text-[#A1A1AA]">RMS</span>
                                    <div className="font-mono text-[#D0F224]">{analysis.rmsLevel} dB</div>
                                </div>
                                <div>
                                    <span className="text-[#A1A1AA]">LUFS</span>
                                    <div className="font-mono text-[#00F0FF]">{analysis.lufs}</div>
                                </div>
                                <div>
                                    <span className="text-[#A1A1AA]">Dynamic</span>
                                    <div className="font-mono text-[#FF9F1C]">{analysis.dynamicRange} dB</div>
                                </div>
                            </div>
                            <div className="mt-4">
                                <span className="text-xs text-[#A1A1AA]">Frequency Balance</span>
                                <div className="flex gap-1 mt-2 h-4">
                                    <div 
                                        className="bg-[#FF3B30] rounded-l" 
                                        style={{ width: `${analysis.lowFreqEnergy}%` }}
                                        title={`Low: ${analysis.lowFreqEnergy}%`}
                                    />
                                    <div 
                                        className="bg-[#D0F224]" 
                                        style={{ width: `${analysis.midFreqEnergy}%` }}
                                        title={`Mid: ${analysis.midFreqEnergy}%`}
                                    />
                                    <div 
                                        className="bg-[#00F0FF] rounded-r" 
                                        style={{ width: `${analysis.highFreqEnergy}%` }}
                                        title={`High: ${analysis.highFreqEnergy}%`}
                                    />
                                </div>
                                <div className="flex justify-between text-xs text-[#A1A1AA] mt-1">
                                    <span>Low</span>
                                    <span>Mid</span>
                                    <span>High</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Center - Waveform & Controls */}
                    <div className="space-y-4">
                        {/* Waveform */}
                        {tracks.length > 0 && tracks[0].buffer && (
                            <Waveform
                                buffer={tracks[0].buffer}
                                currentTime={currentTime}
                                duration={duration}
                                onSeek={seek}
                            />
                        )}

                        {/* Transport Controls */}
                        <div className="flex items-center justify-center gap-4">
                            <Button
                                size="lg"
                                variant="outline"
                                className="w-12 h-12 rounded-full border-[#333]"
                                onClick={stop}
                                disabled={tracks.length === 0}
                                data-testid="stop-btn"
                            >
                                <Square className="w-5 h-5" />
                            </Button>
                            {isPlaying ? (
                                <Button
                                    size="lg"
                                    className="w-16 h-16 rounded-full bg-[#D0F224] text-black hover:bg-[#E2F568]"
                                    onClick={pause}
                                    data-testid="pause-btn"
                                >
                                    <Pause className="w-6 h-6" />
                                </Button>
                            ) : (
                                <Button
                                    size="lg"
                                    className="w-16 h-16 rounded-full bg-[#D0F224] text-black hover:bg-[#E2F568]"
                                    onClick={play}
                                    disabled={tracks.length === 0}
                                    data-testid="play-btn"
                                >
                                    <Play className="w-6 h-6 ml-1" />
                                </Button>
                            )}
                            <div className="font-mono text-lg text-[#D0F224]">
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </div>
                        </div>

                        {/* Timeline Slider */}
                        {duration > 0 && (
                            <Slider
                                value={[currentTime]}
                                onValueChange={([v]) => seek(v)}
                                max={duration}
                                step={0.1}
                                className="w-full"
                                data-testid="timeline-slider"
                            />
                        )}

                        {/* Processing Modules Grid */}
                        <div className="processing-chain">
                            <EQModule settings={settings} onUpdate={updateSetting} />
                            <CompressorModule settings={settings} onUpdate={updateSetting} />
                            <LimiterModule settings={settings} onUpdate={updateSetting} />
                            <StereoModule settings={settings} onUpdate={updateSetting} />
                            <ExciterModule settings={settings} onUpdate={updateSetting} />
                            <DeesserModule settings={settings} onUpdate={updateSetting} />
                        </div>
                    </div>

                    {/* Right Panel - Output */}
                    <div className="space-y-4">
                        {/* Output Module */}
                        <OutputModule settings={settings} onUpdate={updateSetting} />

                        {/* Spectrum Analyzer */}
                        {isPlaying && getAnalyser() && (
                            <div className="control-section">
                                <div className="control-section-header">
                                    <h3 className="font-heading text-sm font-bold">Spectrum</h3>
                                </div>
                                <SpectrumAnalyzer analyser={getAnalyser()} />
                            </div>
                        )}

                        {/* Level Meters */}
                        <div className="control-section">
                            <div className="control-section-header">
                                <h3 className="font-heading text-sm font-bold">Output Level</h3>
                            </div>
                            <div className="flex justify-center gap-4">
                                <div className="text-center">
                                    <LevelMeter level={analysis.rmsLevel} peak={analysis.peakLevel} />
                                    <span className="text-xs text-[#A1A1AA] mt-2 block">L</span>
                                </div>
                                <div className="text-center">
                                    <LevelMeter level={analysis.rmsLevel} peak={analysis.peakLevel} />
                                    <span className="text-xs text-[#A1A1AA] mt-2 block">R</span>
                                </div>
                            </div>
                            <div className="text-center mt-4">
                                <span className="text-xs text-[#A1A1AA]">LUFS</span>
                                <div className="font-mono text-2xl font-bold text-[#00F0FF]">
                                    {analysis.lufs}
                                </div>
                            </div>
                        </div>

                        {/* Export Button */}
                        <Button
                            className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/80 font-bold"
                            disabled={tracks.length === 0}
                            data-testid="export-btn"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Export Master
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
}
